import { isoString } from "../time.js";
import type { Task, TaskPatch } from "./types.js";

/** Local wall-clock hours the relative presets anchor to. */
export interface SnoozeAnchors {
  /** Hour-of-day (0–23) "morning" presets land on (e.g. 9). */
  morningHour: number;
  /** Hour-of-day (0–23) "this evening" lands on (e.g. 18). */
  eveningHour: number;
}

export const DEFAULT_SNOOZE_HOURS: SnoozeAnchors = { morningHour: 9, eveningHour: 18 };

export interface SnoozeTarget {
  id: string;
  label: string;
  /** ISO-8601 UTC wake instant. */
  atIso: string;
}

/** A copy of `base` with the local time set to `hour:00:00.000`, on a day
 *  `addDays` from `base`. Wall-clock — "tomorrow morning" means the user's 9am. */
function atLocalHour(base: Date, addDays: number, hour: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + addDays);
  d.setHours(hour, 0, 0, 0);
  return d;
}

/** Days to add to `from` to reach the next given weekday (0=Sun…6=Sat),
 *  strictly in the future relative to that weekday's anchored time today. */
function daysUntilWeekday(fromDow: number, targetDow: number): number {
  const diff = (targetDow - fromDow + 7) % 7;
  return diff === 0 ? 7 : diff;
}

/**
 * The standard snooze presets resolved against `now` + the user's anchor hours.
 * Pure (time is an argument). Returns wall-clock-anchored instants as UTC ISO.
 */
export function computeSnoozeTargets(now: Date, anchors: SnoozeAnchors = DEFAULT_SNOOZE_HOURS): SnoozeTarget[] {
  const { morningHour, eveningHour } = anchors;

  const inOneHour = new Date(now.getTime() + 60 * 60_000);
  const laterToday = new Date(now.getTime() + 3 * 60 * 60_000);

  // This evening at eveningHour; if it's already that hour or later, roll to tomorrow.
  const eveningDay = now.getHours() >= eveningHour ? 1 : 0;
  const thisEvening = atLocalHour(now, eveningDay, eveningHour);

  const tomorrowMorning = atLocalHour(now, 1, morningHour);

  // Upcoming Saturday morning (if today is Saturday and before morningHour, today).
  const dow = now.getDay();
  const satToday = atLocalHour(now, 0, morningHour);
  const weekend = dow === 6 && satToday > now ? satToday : atLocalHour(now, daysUntilWeekday(dow, 6), morningHour);

  // Upcoming Monday morning.
  const nextWeek = atLocalHour(now, daysUntilWeekday(dow, 1), morningHour);

  return [
    { id: "in1h", label: "In 1 hour", atIso: isoString(inOneHour) },
    { id: "later", label: "Later today", atIso: isoString(laterToday) },
    { id: "evening", label: "This evening", atIso: isoString(thisEvening) },
    { id: "tomorrow", label: "Tomorrow morning", atIso: isoString(tomorrowMorning) },
    { id: "weekend", label: "This weekend", atIso: isoString(weekend) },
    { id: "nextweek", label: "Next week", atIso: isoString(nextWeek) },
  ];
}

/** Patch that snoozes a task until `atIso` (defer-the-nag). */
export function snoozePatch(atIso: string): Pick<TaskPatch, "status" | "snoozed_until"> {
  return { status: "snoozed", snoozed_until: atIso };
}

/**
 * Of `tasks`, the snoozed ones whose wake time has passed, returned as patched
 * copies back in the "open" state (snooze cleared, `updated_at` bumped). Pure —
 * the caller persists the returned rows. Tasks not needing a wake are omitted.
 */
export function wakeSnoozed(tasks: ReadonlyArray<Task>, now: Date): Task[] {
  const nowMs = now.getTime();
  const ts = isoString(now);
  const woken: Task[] = [];
  for (const t of tasks) {
    if (t.status !== "snoozed") continue;
    if (t.snoozed_until && new Date(t.snoozed_until).getTime() > nowMs) continue;
    woken.push({ ...t, status: "open", snoozed_until: null, updated_at: ts });
  }
  return woken;
}
